/**
 * Chatbot in the modal window. Triggered by instance method `startAI`.
 */
import * as Sentry from '@sentry/browser';
import {
    AIChatbotAssistantType,
    AIChatbotEvents,
    AIChatbotQuestionSource,
    trackAIChatbotEvent,
} from 'Analytics/ai-chatbot-events';
import { buildURL, fetchRequest } from 'Utils/api-fetch-request';
import VuMediChatbotBase from './base';

const siteDisplay = document.head.querySelector('meta[name="site-display"]')?.content;

const Copy = {
    cta: 'Get answers and find the latest expert videos from across Vumedi',
    placeholder: {
        default: 'Ask a question...',
        loading: 'Searching...',
        suggestions: `Popular ${siteDisplay} Questions`,
    },
};

/**
 * Map question type to question source property for Mixpanel events.
 * Mirrors iOS event source property.
 */
const _getQuestionSource = (type) => {
    const sourceMap = {
        predefined: AIChatbotQuestionSource.PREDEFINED_QUESTION,
        next_best_question: AIChatbotQuestionSource.NEXT_BEST_QUESTION,
        user_question: AIChatbotQuestionSource.CUSTOM_QUESTION,
    };
    return sourceMap[type] || null;
};

export default class VuMediChatbotModal extends VuMediChatbotBase {
    constructor() {
        super();

        this.predefinedQuestions = [];
        this.triggerElement = null;
        this.eventAssistantType = AIChatbotAssistantType.GENERAL;
    }

    _handleStreamingAnswerResponse(answerElement, question, type = '') {
        return new Promise((resolve) => {
            this.activeEventSource = new EventSource(
                `/api/chats/${this.uuid}/ask/?question=${encodeURIComponent(question)}&question_source=${encodeURIComponent(type)}`,
            );

            this._handleEventSource(this.activeEventSource, answerElement).then(() => resolve());
        });
    }

    async _askQuestion(question, type = '') {
        if (!this.chatbotElement) {
            return;
        }

        this.inProgress = true;

        this.chatbotElement.querySelector('.vm-chatbot-body').classList.add('vm-chatbot-body--conversation');
        this.chatbotElement.querySelector('.vm-chatbot-footer input[type="text"]').value = '';
        this.chatbotElement
            .querySelector('.vm-chatbot-footer input[type="text"]')
            .setAttribute('placeholder', Copy.placeholder.loading);
        this.chatbotElement
            .querySelector('.vm-chatbot-footer button[type="submit"]')
            .setAttribute('disabled', 'disabled');

        trackAIChatbotEvent(AIChatbotEvents.QUESTION_SUBMITTED, {
            assistant_type: this.eventAssistantType,
            question_source: _getQuestionSource(type),
            question,
        });

        this._renderQuestion(question);
        this._toggleSkeleton(true);

        const answerElement = this._answerPrepareElement();

        await this._handleStreamingAnswerResponse(answerElement, question, type);

        if (this.chatbotElement?.hasAttribute('open')) {
            this.chatbotElement.querySelector('.vm-chatbot-footer button[type="submit"]').removeAttribute('disabled');
            this.chatbotElement
                .querySelector('.vm-chatbot-footer input[type="text"]')
                .setAttribute('placeholder', Copy.placeholder.default);
        }

        this.inProgress = false;
        this._toggleSkeleton(false);
    }

    _handleCloseClick() {
        if (!this.chatbotElement) {
            return;
        }

        this.chatbotElement.close();
        this.chatbotElement.querySelector('.vm-chatbot-body').classList.remove('vm-chatbot-body--conversation');

        if (this.activeEventSource) {
            try {
                this.activeEventSource.close();
                this.activeEventSource = null;
                this.inProgress = false;
            } catch (error) {
                Sentry.captureException(error);
            }
        }

        document.body.style.overflow = '';

        const messages = this.chatbotElement.querySelectorAll('.js_vmChatbotMessage');

        if (!messages?.length) {
            return;
        }

        messages.forEach((el) => el.remove());
    }

    _renderModalElement(suggestions = []) {
        const suggestionsMarkup = suggestions
            .map((q, index) =>
                this._getSuggestionMarkup(index + 1, q.type, q.question, 'js_chatbotPlaceholderSuggestion'),
            )
            .join('');

        const markup = `<dialog class="vm-chatbot-modal-container">
            <div class="vm-chatbot-modal">
                <div class="vm-chatbot-header">
                    ${this._vumediAiLogo()}
                    <button type="button" class="vm-button vm-button--sm vm-button--secondary vm-button--chatbot-close js_vmChatbotClose">
                        <span>Close</span>
                        <i class="icon icon-x"></i>
                    </button>
                </div>
                <div class="vm-chatbot-body js_chatbotBody">
                    <vm-chatbot-loader data-namespace="modal"></vm-chatbot-loader>
                    <div class="vm-chatbot-placeholder">
                        <div class="vm-chatbot-placeholder__icon">
                            <i class="icon icon-chatbot"></i>
                        </div>
                        <h4 class="vm-chatbot-placeholder__title">${Copy.cta}</h4>
                        <form class="vm-chatbot-placeholder__form">
                            <i class="icon icon-chatbot"></i>
                            <input type="text"
                                name="question"
                                class="vm-chatbot-input"
                                autocomplete="off"
                                placeholder="${Copy.placeholder.default}"
                                aria-label="Question for the AI" />
                            <button type="submit" class="vm-button vm-button--sm vm-button--primary vm-button--chatbot-send">
                                <i class="icon icon-arrow-up"></i>
                            </button>
                        </form>
                        <div class="vm-chatbot-placeholder__suggestions js_chatbotPlaceholderSuggestions">
                            <p class="vm-chatbot-placeholder__suggestions-title">${Copy.placeholder.suggestions}</p>
                            ${suggestionsMarkup}
                        </div>
                    </div>
                    <div class="vm-chatbot-messages js_vmChatbotMessages">
                        <vm-chatbot-skeleton data-hidden data-type="modal"></vm-chatbot-skeleton>
                        <div class="js_vmChatbotSpacer"></div>
                    </div>
                </div>
                <div class="vm-chatbot-footer">
                    <form class="vm-chatbot-footer__form">
                        <i class="icon icon-chatbot"></i>
                        <input type="text"
                               name="question"
                               class="vm-chatbot-input"
                               autocomplete="off"
                               placeholder="${Copy.placeholder.default}"
                               data-type="user_question"
                               aria-label="Question for AI" />
                        <button type="submit" class="vm-button vm-button--sm vm-button--primary vm-button--chatbot-send">
                            <i class="icon icon-arrow-up"></i>
                        </button>
                    </form>
                </div>
            </div>
        </dialog>`;

        const template = document.createElement('template');
        template.innerHTML = markup;

        const containerElement = template.content.firstChild;

        containerElement.addEventListener('close', () => this._handleCloseClick());
        containerElement.querySelector('.js_vmChatbotClose').addEventListener('click', (ev) => {
            ev.preventDefault();
            this._handleCloseClick();
        });

        const forms = Array.from(containerElement.querySelectorAll('form'));

        forms.forEach((form) => {
            form.addEventListener('submit', (ev) => {
                ev.preventDefault();

                if (this.inProgress) {
                    return;
                }

                const inputElement = form.querySelector('input[type="text"]');

                const question = inputElement.value.trim();
                inputElement.value = '';

                if (!question) {
                    return;
                }

                inputElement.blur();
                this._askQuestion(question, inputElement.getAttribute('data-type') || '');
            });
        });

        this._dynamicHeightObserver(containerElement);

        return containerElement;
    }

    _renderModal() {
        this.chatbotElement = this._renderModalElement(this.predefinedQuestions);
        document.body.appendChild(this.chatbotElement);
        this._placeholderSuggestionsClickHandler(this.chatbotElement);
    }

    _initializeChatbot() {
        return new Promise((resolve, reject) => {
            const requestURL = buildURL('/api/chats/chat-configuration/', { content_type: 'searchiteration' });

            fetchRequest(requestURL)
                .then((data) => {
                    if (data['predefined_questions'] && Array.isArray(data['predefined_questions'])) {
                        this.predefinedQuestions = data['predefined_questions'];
                    }

                    resolve();
                })
                .catch((error) => {
                    reject();
                    Sentry.captureException(error);
                });
        });
    }

    async initialize() {
        try {
            await this._initializeChatbot();
            this._renderModal();

            trackAIChatbotEvent(AIChatbotEvents.LOADED, {
                assistant_type: this.eventAssistantType,
            });

            return true;
        } catch (error) {
            Sentry.captureException(error);
            return false;
        }
    }

    startAI(question = '') {
        if (!this.chatbotElement) {
            return;
        }

        // this is a failsafe to remove any previous messages which might have been left in the modal
        // this can happen if the user closes the modal before the answer was received
        Array.from(this.chatbotElement.querySelectorAll('.vm-chatbot-message')).forEach((el) => {
            el.remove();
        });

        this.chatbotElement.querySelector('.vm-chatbot-body').classList.remove('vm-chatbot-body--conversation');
        this.chatbotElement.querySelector('.vm-chatbot-body').classList.add('is-loading');

        if (!this.chatbotElement.hasAttribute('open')) {
            this.chatbotElement.setAttribute('data-state', 'ready');
            this.chatbotElement.showModal();
            document.body.style.overflow = 'hidden';
        }

        trackAIChatbotEvent(AIChatbotEvents.OPENED, {
            assistant_type: this.eventAssistantType,
        });

        const options = {
            method: 'POST',
            body: JSON.stringify({
                content_type: 'searchiteration',
            }),
        };

        fetchRequest('/api/chats/', options)
            .then((data) => {
                if (!data?.uuid) {
                    Sentry.captureException(new Error('Invalid response from the server'));
                    return;
                }

                this.uuid = data.uuid;
                this.inProgress = false;

                this.chatbotElement.querySelector('.vm-chatbot-body').classList.remove('is-loading');
                this.chatbotElement.querySelector('button[type="submit"]').removeAttribute('disabled');
                this.chatbotElement.querySelector('input[type="text"]').focus();
                this.chatbotElement
                    .querySelector('input[type="text"]')
                    .setAttribute('placeholder', Copy.placeholder.default);

                if (question && this.chatbotElement) {
                    this.chatbotElement.querySelector('input[type="text"]').value = question;
                    this.chatbotElement.querySelector('input[type="text"]').setAttribute('data-type', 'predefined');
                    this.chatbotElement.querySelector('button[type="submit"]').click();
                } else {
                    this.chatbotElement.querySelector('input[type="text"]').setAttribute('data-type', 'user_question');
                }
            })
            .catch((error) => {
                this.chatbotElement?.close();
                this.chatbotElement?.removeAttribute('data-state');
                document.body.style.overflow = '';
                Sentry.captureException(error);
            });
    }
}
